import React, { useContext, useMemo } from 'react'
import dynamic from 'next/dynamic'
import { ChakraProvider } from '@chakra-ui/react'
import { DEFAULT_PLATFORM_THEME } from '@reward-platform/platform-config'
import { logger } from '~/utils/datadog'
import { CacheProvider } from '@emotion/react'
import createCache from '@emotion/cache'
import { getChakraThemeFromKey } from './chakraUtils'
import { ThemeContextValue, ThemeProviderProps } from './ThemeContext.types'

import '@channel/shared-header-footer/style.css'

const ThemeContext = React.createContext<ThemeContextValue>({
  themeId: DEFAULT_PLATFORM_THEME,
  isAviosTheme: true,
  isWhitelabel: false,
})

export const ThemeProvider = ({
  children,
  themeId,
  shouldDynamicallyLoadCSS = true,
  nonce,
}: React.PropsWithChildren<ThemeProviderProps>) => {
  const styleCache = useMemo(
    () =>
      createCache({
        key: 'chakra-ui',
        nonce,
      }),
    [nonce]
  )

  const altoThemeId = themeId.replace('_', '-')
  const AltoDesignTokens = dynamic(
    async () => {
      try {
        const altoModules = await import(`@alto-avios/alto-tokens/web-ssr/${altoThemeId}/light.css`)
        return altoModules.DesignTokens
      } catch (err: any) {
        logger.error(`Error loading tokens for ${altoThemeId}:`, { ...err }, err)
        return undefined
      }
    },
    { ssr: false }
  )
  const AltoFonts = dynamic(
    async () => {
      try {
        const altoModules = await import(`@alto-avios/alto-tokens/web-ssr/${altoThemeId}/fonts.css`)
        return altoModules.AltoFonts
      } catch (err: any) {
        logger.error(`Error loading fonts for ${altoThemeId}:`, { ...err }, err)
        return undefined
      }
    },
    { ssr: false }
  )

  const contextValue = useMemo<ThemeContextValue>(() => {
    const isWhitelabel = themeId !== 'avios'
    return {
      themeId,
      isAviosTheme: !isWhitelabel,
      isWhitelabel,
    }
  }, [themeId])

  return (
    <ThemeContext.Provider value={contextValue}>
      {shouldDynamicallyLoadCSS ? (
        <>
          <AltoDesignTokens />
          <AltoFonts />
        </>
      ) : null}
      {/* TODO: remove ChakraProvider after converting all components */}
      <CacheProvider value={styleCache}>
        <ChakraProvider theme={getChakraThemeFromKey(contextValue.themeId)}>
          {children}
        </ChakraProvider>
      </CacheProvider>
    </ThemeContext.Provider>
  )
}

export const usePlatformTheme = () => useContext(ThemeContext)

export default ThemeContext
